// Single source of truth lives in @erp/shared-types; re-exported here so the
// many `InboundEmailDTO` importers keep their import path. The label map below
// stays frontend-owned (it's a display concern) and is exhaustive over the type.
import type { InboundEmailService } from '@erp/shared-types';

export type { InboundEmailService };

export const INBOUND_EMAIL_SERVICE_LABEL: Record<InboundEmailService, string> =
  {
    'sendgrid-inbound-webhook': 'SendGrid',
    'microsoft-graph-webhook': 'Azure',
    'microsoft-graph-item-attachment': 'Attached Email',
    'eml-attachment': 'Attached Email (.eml)',
    'microsoft-graph-corpus': 'Corpus Import',
  };

export function getInboundEmailServiceLabel(
  service: InboundEmailService,
): string {
  return INBOUND_EMAIL_SERVICE_LABEL[service] ?? service;
}

/** Minimal shape shared by every inbound-email-like object across the RPC
 * contracts and DTOs (list items, single-email views, export payloads). */
export interface InboundEmailTimestamps {
  receivedAt?: Date | string | null;
  createdAt: Date | string;
}

/**
 * Resolves an inbound email's real-world timestamp for display: `receivedAt`
 * (when the email was actually sent/received) falling back to `createdAt`
 * (DB row-insert time) for legacy rows or endpoints that don't select
 * `receivedAt`. Centralizes this fallback so display call sites don't each
 * repeat it.
 */
export function getInboundEmailReceivedAt(email: InboundEmailTimestamps): Date {
  // Prefer `receivedAt`, but fall back to `createdAt` when it's absent *or*
  // present-but-unparseable, so a bad `receivedAt` doesn't yield an Invalid
  // Date when a usable `createdAt` exists.
  if (email.receivedAt != null) {
    const receivedAt = new Date(email.receivedAt);
    if (!Number.isNaN(receivedAt.getTime())) {
      return receivedAt;
    }
  }
  return new Date(email.createdAt);
}

/**
 * Like {@link getInboundEmailReceivedAt} but returns an ISO string, or
 * `undefined` when neither `receivedAt` nor `createdAt` yields a valid date.
 * Snapshot payloads (e.g. a sales-order draft's `sourceData.email`) can omit
 * both fields; calling `.toISOString()` on the resulting Invalid Date throws
 * `RangeError: Invalid time value`. Use this wherever an ISO string is needed
 * so a missing timestamp degrades to `undefined` instead of crashing.
 */
export function getInboundEmailReceivedAtISO(
  email: InboundEmailTimestamps,
): string | undefined {
  const date = getInboundEmailReceivedAt(email);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

export interface DocumentAssociationDTO {
  associationType: string; // TODO copy type
  createdAt: string;
  document: {
    createdAt: string;
    fileSizeBytes: number;
    id: string;
    mimeType: string;
    name: string;
    originalFileName: string;
    status: string;
    updatedAt: string;
    userId: string | null;
    url: string;
    contentId?: string | null;
  };
  documentId: string;
  entityId: string;
  entityType: string;
  id: string;
  updatedAt: string;
}

export interface InboundEmailDTO {
  id: string;
  messageId: string;
  from: string;
  to: string;
  cc?: string | null;
  subject?: string | null;
  textContent?: string | null;
  htmlContent?: string | null;
  spamScore?: number | null;
  spamReport?: string | null;
  senderIp?: string | null;
  spf?: string | null;
  dkim?: string | null;
  attachmentCount?: number | null;
  service: InboundEmailService;
  receivedByMailbox?: string | null;
  receivedAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
  classification?: {
    id: string;
    classificationResult: string;
    confidenceScore: number;
    reasoning: string | null;
    createdAt: Date;
  } | null;
  attachments?: Array<{
    id: string;
    originalName: string;
    s3Url: string;
    mimeType: string;
    sizeBytes: number;
    contentId?: string | null;
  }>;
  /**
   * Number of split-child documents (parentDocumentId set) associated with
   * this email; always returned so the UI can offer a toggle.
   */
  splitAttachmentsCount?: number;
  /**
   * Split-child documents mapped to the attachment shape; only populated
   * when the request passes includeSplitDocuments=true.
   */
  splitAttachments?: Array<{
    id: string;
    originalName: string;
    s3Url: string;
    mimeType: string;
    sizeBytes: number;
    contentId?: string | null;
  }>;
  conversationId?: string;
  documentAssociations?: Array<DocumentAssociationDTO>;
  extractionResults?: {
    apInvoices?: Array<{
      id: string;
      status: string;
      apInvoiceId: string | null;
      errorDetails?: Record<string, unknown> | null;
      createdAt: Date;
    }>;
    quoteSheets?: Array<{
      id: string;
      status: string;
      quoteSheetId: string | null;
      errorDetails?: Record<string, unknown> | null;
      createdAt: Date;
    }>;
    purchaseOrders?: Array<{
      id: string;
      status: string;
      stagedPurchaseOrderId?: string | null;
      errorDetails?: Record<string, unknown> | null;
      createdAt: Date;
    }>;
  };
  relatedRecords?: {
    salesOrders?: Array<{
      id: string;
      internalId: number;
      status: string;
      orderDate: string;
      customerId: string;
      createdAt: Date;
    }>;
    quoteSheets?: Array<{
      id: string;
      supplierId: string;
      quoteDate: string | null;
      createdAt: Date;
    }>;
    apInvoices?: Array<{
      id: string;
      invoiceNumber: string;
      status: string;
      supplierId: string | null;
      totalAmount: number | string;
      createdAt: Date;
    }>;
  };
}
